import { useLocation, useNavigate } from '@tanstack/react-router';
import { ChartColumnIncreasing, CreditCard, Map, Settings, Users } from 'lucide-react';

import { USER_ROLE } from '@/entities/user/user-constants';
import { WORKSPACE_PLAN } from '@/entities/workspace/workspace-constants';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils/cn';

export function SettingSidebar() {
  const { workspace } = useWorkspace();
  const { workspaceId } = useWorkspaceId();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="w-54 gap-1">
      <SettingSidebarItem
        title="General"
        icon={<Settings className="size-4" />}
        isActive={location.pathname.includes('/settings/general')}
        onClick={() => {
          navigate({
            to: '/workspace/$workspaceId/settings/general',
            params: { workspaceId },
          });
        }}
      />
      <SettingSidebarItem
        title="People"
        icon={<Users className="size-4" />}
        isActive={location.pathname.includes('/settings/member')}
        onClick={() => {
          navigate({ to: '/workspace/$workspaceId/settings/member', params: { workspaceId } });
        }}
      />
      {workspace?.privilege === USER_ROLE.OWNER && (
        <>
          {workspace.plan !== WORKSPACE_PLAN.DEMO && (
            <>
              <SettingSidebarItem
                title="Usage"
                icon={<ChartColumnIncreasing className="size-4" />}
                isActive={location.pathname.includes('/settings/usage')}
                onClick={() => {
                  navigate({
                    to: '/workspace/$workspaceId/settings/usage',
                    params: { workspaceId },
                  });
                }}
              />
              <SettingSidebarItem
                title="Plan"
                icon={<Map className="size-4" />}
                isActive={location.pathname.includes('/settings/plan')}
                onClick={() => {
                  navigate({
                    to: '/workspace/$workspaceId/settings/plan',
                    params: { workspaceId },
                  });
                }}
              />
            </>
          )}
          <SettingSidebarItem
            title="Billing"
            icon={<CreditCard className="size-4" />}
            isActive={location.pathname.includes('/settings/billing')}
            onClick={() => {
              navigate({ to: '/workspace/$workspaceId/settings/billing', params: { workspaceId } });
            }}
          />
        </>
      )}
    </div>
  );
}

export function SettingSidebarItem({
  title,
  icon,
  onClick,
  isActive,
}: {
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <div
      className={cn(
        'hover:bg-muted flex cursor-pointer items-center gap-2 rounded-md p-2 transition-all duration-100',
        isActive && 'bg-muted',
      )}
      onClick={onClick}
    >
      <div className="h-4 w-4">{icon}</div>
      <span className="text-sm font-medium">{title}</span>
    </div>
  );
}
